import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, type OtpChannel } from '@chicare/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { sessionKeys, useSession } from '@/lib/session';
import { Icon } from '@/components/icons/Icon';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';

/**
 * The buyer's front door — two of them, and one code screen.
 *
 * ── CREATING AN ACCOUNT TAKES BOTH CONTACTS ─────────────────────────────────
 *
 * A name, a phone number and an email address, together, before anything else.
 * A phone number alone is not enough to hold a medical order history: SMS in
 * Nigeria is good but not certain, numbers get ported, phones are lost and
 * handsets are shared. A buyer whose only door is a number they no longer
 * control is locked out of their own orders, their own refunds and their own
 * delivery codes — permanently, with nothing support can safely do about it.
 *
 * One code then goes to BOTH channels. From that point either one signs them
 * in, and every code lands in both places.
 *
 * ── WHY SIGNING IN IS A SEPARATE DOOR ───────────────────────────────────────
 *
 * `request-code` answers the same way whether or not it recognises an
 * identifier, because telling an anonymous caller which numbers hold ChiCare
 * accounts is a privacy leak and, for a pharmacy, a medical one. That property
 * only survives if the client never has to ask "is this person new?" — so
 * registering is something a person chooses, not something the API discloses.
 */
export function SignInScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const { user } = useSession();

  const [mode, setMode] = useState<'signIn' | 'register'>(
    params.get('new') === '1' ? 'register' : 'signIn',
  );

  const [identifier, setIdentifier] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [contact, setContact] = useState('');

  const [step, setStep] = useState<'form' | 'code' | 'finish'>('form');
  const [sentTo, setSentTo] = useState('');
  const [channels, setChannels] = useState<OtpChannel[]>([]);
  const [missing, setMissing] = useState<Array<'name' | 'phone' | 'email'>>([]);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const codeInput = useRef<HTMLInputElement>(null);

  const intended = (location.state as { from?: string } | null)?.from ?? '/';

  const registering = mode === 'register';

  useEffect(() => {
    // `finish` runs AFTER the token exists, so the redirect that fires on a
    // live session has to hold off until the buyer is actually done.
    if (user && step !== 'finish') navigate(intended, { replace: true });
  }, [user, step, intended, navigate]);

  // The resend countdown. Showing "Resend" while it would be refused is worse
  // than showing a timer.
  useEffect(() => {
    if (secondsLeft <= 0) return;

    const timer = window.setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);

    return () => window.clearInterval(timer);
  }, [secondsLeft]);

  function onCodeSent(challenge: {
    sent_to: string;
    channels: OtpChannel[];
    resend_in: number;
    identifier: string;
  }) {
    setStep('code');
    setSentTo(challenge.sent_to);
    setChannels(challenge.channels);
    setSecondsLeft(challenge.resend_in);
    // A registration is keyed on the phone number the API normalised, not on
    // whatever shape the buyer typed it in.
    if (registering) setIdentifier(challenge.identifier);
    window.setTimeout(() => codeInput.current?.focus(), 50);
  }

  const requestCode = useMutation({
    mutationFn: () =>
      registering ? api.register({ name, phone, email }) : api.requestLoginCode(identifier),
    onSuccess: onCodeSent,
  });

  const verify = useMutation({
    mutationFn: () =>
      api.verifyLoginCode({
        identifier,
        code,
        purpose: registering ? 'register' : 'login',
      }),
    onSuccess: (session) => {
      queryClient.setQueryData(sessionKeys.me(), session.user);

      if (session.profile_complete) {
        navigate(intended, { replace: true });

        return;
      }

      // Only reachable for an account made before both contacts were required.
      // They are signed in either way — this is an ask, not a gate.
      setMissing(session.missing);
      setContact('');
      setStep('finish');
    },
  });

  /**
   * The ONE field this screen is asking for, out of what the API says is
   * missing.
   *
   * ── WHY THIS IS NOT A TERNARY ANY MORE ────────────────────────────────────
   *
   * It used to be `missing.includes('phone') ? phone : email` — in the heading,
   * the label, the input type, the placeholder, the error and the mutation,
   * six times over. A binary, for a list that has always had THREE values.
   *
   * So an account missing only its NAME fell down the else branch and was told
   * "Your account has no email on it", under a box asking for an email it
   * already had. Saving did nothing, because the email was already that value,
   * and the screen came back on the next sign-in. For ever.
   *
   * Reported from the live site on 12 Oct 2026 by somebody whose account read
   * `ChiCare buyer | +2349077100490 | okonjie57@gmail.com` — complete apart
   * from the name, and asked for the one thing it was not short of.
   *
   * Name first: it is the one that goes on the delivery, and it is the one a
   * ghost account is always missing.
   */
  const asking: 'name' | 'phone' | 'email' = missing.includes('name')
    ? 'name'
    : missing.includes('phone')
      ? 'phone'
      : 'email';

  const finish = useMutation({
    mutationFn: () =>
      api.updateProfile(
        asking === 'name'
          ? { name: contact.trim() }
          : asking === 'phone'
            ? { phone: contact.trim() }
            : { email: contact.trim() },
      ),
    onSuccess: (result) => {
      queryClient.setQueryData(sessionKeys.me(), result.user);
      navigate(intended, { replace: true });
    },
  });

  const active = step === 'form' ? requestCode : step === 'code' ? verify : finish;
  const fieldErrors = active.error instanceof ApiError ? active.error.fieldErrors : {};

  /** "WhatsApp, SMS and your email" — where to actually look. */
  const whereItWent = useMemo(() => {
    const names: Record<OtpChannel, string> = {
      whatsapp: 'WhatsApp',
      sms: 'SMS',
      email: 'your email',
    };

    const list = channels.map((channel) => names[channel]).filter(Boolean);

    if (list.length === 0) return null;
    if (list.length === 1) return list[0];

    return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
  }, [channels]);

  function switchMode(next: 'signIn' | 'register') {
    setMode(next);
    setStep('form');
    setCode('');
    requestCode.reset();
    verify.reset();
  }

  /**
   * The code was right, but there is no account behind the contact.
   *
   * The API cannot say this at request-code time without telling anybody who
   * asks whether a given number shops here, so it says it now — after the
   * person has proved they control the contact, when it discloses nothing.
   *
   * A dead end here would be cruel: they have just read a code out of a text
   * message to be told "no". So the screen turns into the registration form
   * with what they typed already in it, and they finish in one more step.
   */
  const noAccount =
    verify.error instanceof ApiError && verify.error.code === 'NO_ACCOUNT';

  const canSubmitForm = registering
    ? name.trim().length > 1 && phone.trim().length >= 10 && email.includes('@')
    : identifier.trim().length >= 6;

  return (
    <>
      <AppBar back title={registering ? 'Create your account' : 'Sign in'} />

      {/* ── CENTRED ON A PHONE, NOT PINNED TO THE TOP ──
          The card used to start 24px under the app bar, which on a phone left
          most of the screen empty below it and the form crammed against the
          chrome above. A sign-in form is the only thing on its screen, so it
          should sit where the eye already is.

          `min-h` rather than `h`: the register step is taller than the sign-in
          step and the code step taller again, so a fixed height would centre
          the short one and clip the tall one. This centres while there is room
          and scrolls normally once there is not.

          The height subtracts the app bar and the bottom nav — without that the
          card is centred in a box taller than the visible area and ends up
          sitting too low, under the tab bar. `svh` rather than `vh` because on
          mobile Safari `vh` includes the browser chrome that is not there. */}
      <div className="mx-auto flex w-full max-w-[440px] flex-col justify-center px-4 py-6 min-h-[calc(100svh-132px)] sm:min-h-0 sm:py-10">
        <Card className="!p-6 sm:!p-7">
          {step === 'form' ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                requestCode.mutate();
              }}
            >
              <h1 className="m-0 font-display text-[21px] font-bold tracking-[-0.02em]">
                {registering ? 'Create your ChiCare account' : 'Welcome back'}
              </h1>
              {/* One line, and the shortest one that still answers "why are you
                  asking for both". Two sentences of reassurance above a form is
                  two sentences nobody reads — and on this screen the person has
                  already decided to sign in, so there is nothing left to
                  persuade them of. */}
              <p className="mt-1.5 text-[13px] leading-relaxed text-muted">
                {registering
                  ? 'Your email and phone number — we send the code to both.'
                  : 'Your phone number or your email.'}
              </p>

              <div className="mt-5 space-y-4">
                {registering ? (
                  <>
                    <Input
                      label="Your name"
                      hint="It goes on the delivery so the rider knows who to ask for."
                      autoComplete="name"
                      autoFocus
                      required
                      value={name}
                      placeholder="Adaeze Okonkwo"
                      error={fieldErrors.name?.[0]}
                      onChange={(event) => setName(event.target.value)}
                    />

                    <Input
                      label="Phone number"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      required
                      value={phone}
                      placeholder="0803 123 4567"
                      error={fieldErrors.phone?.[0]}
                      onChange={(event) => setPhone(event.target.value)}
                    />

                    <Input
                      label="Email address"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      required
                      value={email}
                      placeholder="you@example.com"
                      error={fieldErrors.email?.[0]}
                      onChange={(event) => setEmail(event.target.value)}
                    />
                  </>
                ) : (
                  <Input
                    label="Phone number or email"
                    inputMode="email"
                    autoComplete="username"
                    autoFocus
                    required
                    value={identifier}
                    placeholder="0803 123 4567"
                    error={
                      fieldErrors.identifier?.[0] ??
                      fieldErrors.phone?.[0] ??
                      fieldErrors.email?.[0]
                    }
                    onChange={(event) => setIdentifier(event.target.value)}
                  />
                )}
              </div>

              <Button
                full
                type="submit"
                className="mt-4"
                loading={requestCode.isPending}
                disabled={!canSubmitForm}
              >
                Send me a code
              </Button>

              <p className="m-0 mt-4 text-center text-[12.5px] text-muted">
                {registering ? 'Already have an account? ' : 'First time here? '}
                <button
                  type="button"
                  onClick={() => switchMode(registering ? 'signIn' : 'register')}
                  className="min-h-11 px-1 text-[12.5px] font-bold text-blue"
                >
                  {registering ? 'Sign in' : 'Create an account'}
                </button>
              </p>
            </form>
          ) : step === 'code' ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                verify.mutate();
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setStep('form');
                  setCode('');
                  verify.reset();
                }}
                className="-mx-1 mb-3 inline-flex min-h-9 items-center gap-1.5 rounded-btn px-1 text-[12px] font-medium text-blue"
              >
                <Icon name="back" size={14} strokeWidth={2.4} />
                Change that
              </button>

              <h1 className="m-0 font-display text-[21px] font-bold tracking-[-0.02em]">
                Enter your code
              </h1>
              <p className="mt-1.5 text-[13px] leading-relaxed text-muted">
                {/* Both destinations, masked. The cheapest typo check there is:
                    somebody hunting for a code that went to the wrong address
                    sees the wrong address while they are hunting. */}
                Sent to <b className="text-ink">{sentTo}</b>
                {whereItWent ? (
                  <>
                    {' '}
                    — check <b className="text-ink">{whereItWent}</b>
                  </>
                ) : null}
                . It expires in five minutes.
              </p>

              <div className="mt-5">
                <Input
                  ref={codeInput}
                  label="6-digit code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  mono
                  required
                  maxLength={6}
                  value={code}
                  placeholder="••••••"
                  className="text-center text-[20px] tracking-[0.3em]"
                  error={fieldErrors.code?.[0]}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
                />
              </div>

              <Button
                full
                type="submit"
                className="mt-4"
                loading={verify.isPending}
                disabled={code.length < 6}
              >
                {registering ? 'Create my account' : 'Sign in'}
              </Button>

              <div className="mt-3 text-center">
                {secondsLeft > 0 ? (
                  <span className="text-[12px] text-faint">Resend in {secondsLeft}s</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => requestCode.mutate()}
                    className="min-h-11 px-2 text-[12px] font-bold text-blue"
                  >
                    Send another code
                  </button>
                )}
              </div>
            </form>
          ) : (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                finish.mutate();
              }}
            >
              <h1 className="m-0 font-display text-[21px] font-bold tracking-[-0.02em]">
                You&apos;re in. One last thing.
              </h1>
              <p className="mt-1.5 text-[13px] leading-relaxed text-muted">
                {asking === 'name'
                  ? 'We do not have your name yet. It goes on the delivery, so the rider knows who to ask for at your door.'
                  : asking === 'phone'
                    ? 'Your account has no phone number on it. A rider needs one to reach you at your door.'
                    : 'Your account has no email on it. It gives us a second place to send your codes, so a text that never arrives cannot lock you out.'}
              </p>

              <div className="mt-5">
                <Input
                  label={
                    asking === 'name'
                      ? 'Your name'
                      : asking === 'phone'
                        ? 'Phone number'
                        : 'Email address'
                  }
                  type={asking === 'phone' ? 'tel' : asking === 'email' ? 'email' : 'text'}
                  inputMode={asking === 'phone' ? 'tel' : asking === 'email' ? 'email' : 'text'}
                  autoComplete={
                    asking === 'name' ? 'name' : asking === 'phone' ? 'tel' : 'email'
                  }
                  autoFocus
                  value={contact}
                  placeholder={
                    asking === 'name'
                      ? 'Okonji Emmanuel'
                      : asking === 'phone'
                        ? '0803 123 4567'
                        : 'you@example.com'
                  }
                  error={fieldErrors[asking]?.[0]}
                  onChange={(event) => setContact(event.target.value)}
                />
              </div>

              <Button
                full
                type="submit"
                className="mt-4"
                loading={finish.isPending}
                disabled={!contact.trim()}
              >
                Save and continue
              </Button>

              {/* They already hold a token. Trapping them behind this would be
                  a lie about what that token means — and this is an older
                  account being brought up to the current rules, not a new one. */}
              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={() => navigate(intended, { replace: true })}
                  className="min-h-11 px-2 text-[12px] font-bold text-muted"
                >
                  Skip for now
                </button>
              </div>
            </form>
          )}

          {/* ────────────────── A STRANGER, NOT A FAILURE ──────────────────
              The code was right; there is simply no account behind the contact.
              A bare red line here would be cruel — they have just read a code
              out of a text message to be told "no" — so this is a way forward
              rather than a dead end, with what they typed carried across.

              This is also the ONLY place the API will say it. /auth/request-code
              must never disclose whether a number is known, or anybody could
              walk a list to find out who shops here. */}
          {noAccount ? (
            <div
              role="alert"
              data-testid="no-account"
              className="mt-4 rounded-card border border-blue bg-blue-tint p-3.5"
            >
              <p className="m-0 text-[13px] font-bold text-ink">
                You do not have a ChiCare account yet
              </p>
              <p className="m-0 mt-1 text-[12.5px] leading-relaxed text-muted">
                Creating one takes a name, a phone number and an email — about a minute. Your
                {identifier.includes('@') ? ' email' : ' number'} is already filled in.
              </p>

              <Button
                full
                className="mt-3"
                onClick={() => {
                  // Carry the proved contact into the right box rather than
                  // making somebody type it a third time.
                  if (identifier.includes('@')) setEmail(identifier);
                  else setPhone(identifier);

                  setMode('register');
                  setStep('form');
                  verify.reset();
                }}
              >
                Create my account
              </Button>
            </div>
          ) : null}

          {active.error instanceof ApiError && !active.error.isValidation && !noAccount ? (
            <p role="alert" className="mt-3 text-[12px] font-medium text-danger">
              {active.error.message}
            </p>
          ) : null}
        </Card>

        {/* Kept, shortened. What it is FOR is the one thing somebody handing
            over two contacts to a pharmacy app actually wants to know, and it
            is a promise rather than reassurance — but it does not need two
            sentences to make it. */}
        <p className="mt-4 flex items-start gap-1.5 px-1 text-[11px] leading-relaxed text-faint">
          Used only to sign you in and to let your rider reach you. Never shown to a pharmacy
          before you order.
        </p>

        {import.meta.env.DEV ? (
          <div className="mt-3 rounded-card border border-dashed border-line bg-white/60 p-3.5">
            <p className="m-0 mb-2 font-mono text-[10px] uppercase tracking-[0.08em] text-faint">
              Local development only
            </p>
            <p className="m-0 text-[11px] leading-relaxed text-muted">
              Read the code out of{' '}
              <b className="font-mono text-ink">api/storage/logs/laravel.log</b> — the SMS gateway
              and the mailer both write there instead of sending.
            </p>
          </div>
        ) : null}
      </div>
    </>
  );
}
